import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#FBFBF9] px-6 py-8">
    <Logo />
    <div className="mx-auto mt-24 max-w-md rounded-md border border-stone-200 bg-white p-10 text-center rise">{children}</div>
  </div>
);

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking");

  useEffect(() => {
    const sid = params.get("session_id");
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { await refresh(); return setState("paid"); }
        if (["expired", "failed"].includes(data.payment_status)) return setState("failed");
      } catch { /* retry */ }
      if (++tries >= 15) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    if (sid) poll(); else setState("failed");
    return () => clearTimeout(timer);
  }, [params, refresh]);

  return (
    <Shell>
      <div data-testid="payment-status" data-state={state}>
        {state === "checking" && <><Loader2 className="mx-auto h-10 w-10 animate-spin text-stone-400" /><p className="mt-6 font-display text-2xl font-bold">Confirming your payment…</p></>}
        {state === "paid" && (params.get("kind") === "fee"
          ? <><CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" /><p className="mt-6 font-display text-2xl font-bold">Placement fee paid</p><p className="mt-2 text-sm text-stone-600">Thank you. Our team will finalise onboarding with your new worker.</p></>
          : params.get("kind") === "job"
          ? <><CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" /><p className="mt-6 font-display text-2xl font-bold">Job ad featured</p><p className="mt-2 text-sm text-stone-600">Your ad shows at the top of the candidate job board for 30 days once approved.</p></>
          : <><CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" /><p className="mt-6 font-display text-2xl font-bold">Membership activated</p><p className="mt-2 text-sm text-stone-600">You now have full access to candidates, hiring and business files.</p></>)}
        {state === "failed" && <><XCircle className="mx-auto h-12 w-12 text-red-500" /><p className="mt-6 font-display text-2xl font-bold">Payment not completed</p></>}
        {state === "timeout" && <><Loader2 className="mx-auto h-10 w-10 text-stone-400" /><p className="mt-6 font-display text-2xl font-bold">Still processing</p><p className="mt-2 text-sm text-stone-600">Refresh this page in a minute or contact our team.</p></>}
        <Link to={params.get("kind") === "job" ? `/app/jobs/${params.get("job")}` : params.get("kind") === "fee" ? "/app/requests" : "/app"} data-testid="payment-go-dashboard" className="mt-8 inline-block rounded-md bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-700">{params.get("kind") === "job" ? "Back to job ad" : "Go to portal"}</Link>
      </div>
    </Shell>
  );
};

export const PaymentCancel = () => (
  <Shell>
    <XCircle className="mx-auto h-12 w-12 text-stone-400" />
    <p className="mt-6 font-display text-2xl font-bold" data-testid="payment-cancelled">Checkout cancelled</p>
    <p className="mt-2 text-sm text-stone-600">No charge was made. You can pick a plan any time.</p>
    <Link to="/app/membership" data-testid="payment-back-plans" className="mt-8 inline-block rounded-md bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-700">Back to plans</Link>
  </Shell>
);
